<style scoped>
#cefr-table {
  width: 100%;
  overflow-x: hidden;
}

.content {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
}
</style>

<template>
  <div id="app">
    <div v-if="loading">
      <CardHeader :title="$t('conversationAnalysis.title').toString()" />
      <div class="content d-flex flex-column pa-3 text-center" style="gap: 8px">
        <div class="text-center">
          <v-progress-circular indeterminate color="grey"></v-progress-circular>
        </div>
        <div class="text-body2">{{ $t('conversationAnalysis.loading') }}</div>
      </div>
    </div>
    <div v-else-if="error">
      <CardHeader :title="$t('conversationAnalysis.title').toString()" />
      <div class="content d-flex flex-column pa-3" style="gap: 20px">
        <p class="mb-0">
          {{ $t('conversationAnalysis.conversationError') }}
        </p>
      </div>
    </div>
    <div v-else>
      <CardHeader :title="$t('conversationAnalysis.title').toString()">
        <div class="px-3">
          <v-tabs background-color="#1b2643" color="#eeeeee">
            <v-tab>{{ $t('conversationAnalysis.transcript') }}</v-tab>
          </v-tabs>
        </div>
      </CardHeader>
      <div class="content d-flex flex-column pa-10" style="gap: 20px">
        <div>
          <p class="text-h6">{{ $t('conversationAnalysis.couponCode') }}</p>
          {{ couponCode }}
        </div>
        <div>
          <p class="text-h6">{{ $t('conversationAnalysis.dialogID') }}</p>
          {{ dialogId }}
        </div>

        <div
          v-if="
            dialog?.assessment?.assessmentData &&
            dialog.gnowsisGroupTypes.includes('speaking-test-assessment')
          "
          style="width: 800px"
        >
          <p class="text-h6">{{ $t('conversationAnalysis.cefrLevels') }}</p>
          <v-simple-table id="cefr-table">
            <template v-slot:default>
              <thead>
                <tr>
                  <th
                    v-for="(header, index) in cefrTableHeaders"
                    :key="index"
                    v-html="header"
                    class="text-center"
                  ></th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td
                    v-for="(cefrVal, index) in cefrTableBody"
                    :key="index"
                    :class="cefrStyleClass(cefrVal.label)"
                  >
                    {{ cefrVal.label }}
                    <span v-if="cefrVal.value !== -1"
                      >({{ cefrVal.value }})</span
                    >
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>

        <div
          v-if="
            dialog?.canDo &&
            dialog.gnowsisGroupTypes.includes('can-do') &&
            dialog.canDo.success === true
          "
        >
          <p class="text-h6">
            {{ $t('conversationAnalysis.canDo.label') }} {{ canDoRatio }}
          </p>
          <div
            v-for="canDo in sortedCanDos"
            :key="canDo.canDo.en"
            class="d-flex align-center"
            style="gap: 8px"
          >
            <v-icon
              :style="{ color: canDo.color }"
              :title="canDo.achievementLabel"
              :aria-label="canDo.achievementLabel"
              role="img"
              aria-hidden="false"
              >{{ canDo.icon }}</v-icon
            >
            <span class="font-weight-bold">{{
              $i18n.locale === 'ja' ? canDo.canDo.ja : canDo.canDo.en
            }}</span>
          </div>
        </div>

        <div
          v-if="
            dialog?.review &&
            dialog.review.isFinished === true &&
            dialog.gnowsisGroupTypes.includes('review-feedback') &&
            dialog.review.reviewCards.length > 0
          "
        >
          <p class="text-h6">
            {{ $t('conversationAnalysis.review') }} {{ reviewPracticeRatio }}
          </p>
          <v-expansion-panels accordion multiple>
            <v-expansion-panel v-for="card in sortedReviewCards" :key="card.id">
              <v-expansion-panel-header>
                <div class="d-flex align-center" style="gap: 8px">
                  <v-icon>{{
                    card.practicedAt !== null
                      ? 'mdi-check-bold'
                      : 'mdi-clock-outline'
                  }}</v-icon>
                  <span class="font-weight-bold">
                    {{
                      card.reviewCardData.targetSystemUtteranceEnglishText
                    }}</span
                  >
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="d-flex flex-column" style="gap: 12px">
                  <div>
                    <p class="text-subtitle-2 mb-1">
                      {{ $t('conversationAnalysis.suggestedResponse') }}
                    </p>
                    <p class="mb-0">
                      {{ card.reviewCardData.feedbackEnglishText }}
                    </p>
                  </div>
                  <div>
                    <p class="text-subtitle-2 mb-1">
                      {{ $t('conversationAnalysis.studentsResponse') }}
                    </p>
                    <p class="mb-0">
                      {{ card.reviewCardData.targetUserUtteranceEnglishText }}
                    </p>
                  </div>
                  <div>
                    <p class="text-subtitle-2 mb-1">
                      {{ $t('conversationAnalysis.reviewAdvice') }}
                    </p>
                    <p class="mb-0">
                      {{
                        $i18n.locale === 'ja'
                          ? card.reviewCardData.adviceJapaneseText
                          : card.reviewCardData.adviceEnglishText
                      }}
                    </p>
                  </div>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>

        <div>
          <p class="text-h6">
            {{ $t('conversationAnalysis.conversationAudio') }}
          </p>
          <audio
            v-if="isAudioPlayerShown"
            controls
            style="width: 100%"
            preload="auto"
            controlsList="nodownload"
          >
            <source :src="audio" type="audio/mpeg" />
            Your browser does not support the audio element.
          </audio>
          <div v-else>
            <p>{{ $t('conversationAnalysis.noAudioAvailable') }}</p>
          </div>
        </div>

        <div id="app">
          <p class="text-h6">
            {{ $t('conversationAnalysis.conversationTranscripts') }}
          </p>
          <div v-if="transcriptTableBody.length === 0">
            <p>{{ $t('conversationAnalysis.noTranscriptsAvailable') }}</p>
          </div>

          <div v-else>
            <p class="text-body-2" v-html="formattedCaptionText"></p>
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th
                      v-for="(header, index) in transcriptTableHeaders"
                      align="center"
                      :key="index"
                      v-html="header"
                      nowrap=""
                    ></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in transcriptTableBody" :key="row.turnId">
                    <template v-if="row.isFirstInTopic">
                      <td
                        :rowspan="row.rowSpan"
                        class="text-left align-middle"
                        nowrap=""
                      >
                        {{ row.topic }}
                      </td>
                    </template>
                    <td align="center">{{ row.turnId }}</td>
                    <td align="center">{{ row.startTime }}</td>
                    <td align="center">{{ row.endTime }}</td>
                    <td align="left">
                      <v-icon small left>{{ row.icon }}</v-icon>
                      <span
                        :class="{
                          'font-italic': row.icon === 'mdi-robot',
                          'font-weight-bold': row.icon === 'mdi-account',
                        }"
                      >
                        {{ row.text }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "@vue/composition-api";
import { dialogService } from "@/api/dialogService";
import CardHeader from "@/components/Common/CardHeader.vue";
import {
  cefrStyleClass,
  roundToTwoDecimals,
} from "@/components/utils/commonUtils";
import type { AudioFileResponse } from "@/resources/audio";
import type {
  AssessmentData,
  CanDoAchievement,
  CategoryWithDescription,
  DialogWithRelations,
} from "@/resources/dialog";
import type { CefrTableBody, TranscriptTableBody } from "@/resources/table";
import store from "@/store";
import {
  getCanDoScoreRatio,
  getReviewPracticeCount,
  toAchievement,
} from "@/utils/transformCampaignResult";

const STATUS_STYLES: Record<
  CanDoAchievement,
  { icon: string; color: string; labelKey: string }
> = {
  success: {
    icon: "mdi-check-bold",
    color: "#3F9142",
    labelKey: "conversationAnalysis.canDo.status.success",
  },
  partial: {
    icon: "mdi-triangle-outline",
    color: "#A9811F",
    labelKey: "conversationAnalysis.canDo.status.partial",
  },
  fail: {
    icon: "mdi-close-thick",
    color: "#D64560",
    labelKey: "conversationAnalysis.canDo.status.fail",
  },
};

type CanDoListItem = {
  canDo: { en: string; ja: string };
  achievement: CanDoAchievement;
  icon: string;
  color: string;
  achievementLabel: string;
};

export default defineComponent({
  components: {
    CardHeader,
  },
  name: "ConversationAnalysis",
  data() {
    return {
      loading: true,
      couponCode: "",
      dialogId: this.$route.params.dialogId,
      userId: "",
      cefrTableHeaders: [] as string[],
      cefrTableBody: [] as CefrTableBody[],
      transcriptTableHeaders: [] as string[],
      transcriptTableBody: [] as TranscriptTableBody[],
      error: false,
      dialog: null as DialogWithRelations | null,
      audio: undefined as string | undefined,
      isAudioPlayerShown: true,
    };
  },
  watch: {
    "$i18n.locale"() {
      this.updateHeaders();
      if (this.dialog?.assessment?.assessmentData) {
        this.populateCefrTable(this.dialog.assessment.assessmentData);
      }
    },
  },
  created() {
    this.updateHeaders();
  },
  async mounted() {
    const customerId = store.state.customerId;

    try {
      const dialog = await dialogService.getById(this.dialogId);

      if (!dialog || dialog.customerIdLegacy !== customerId) {
        this.error = true;
        return;
      }

      const ticket = dialog.ticket;

      this.couponCode = ticket?.customerOptions?.couponCode || "";
      this.userId = dialog.learnerId;
      this.dialog = dialog;

      if (dialog.assessment?.assessmentData) {
        this.populateCefrTable(dialog.assessment.assessmentData);
      }

      await this.fetchTranscript();

      await this.getAudioTranscript();
    } catch (error) {
      console.log(error);
    } finally {
      this.loading = false;
    }
  },
  computed: {
    formattedCaptionText() {
      const rawText = this.$t("conversationAnalysis.captionText").toString();
      const parts = rawText.split("*");
      return `
      <span class="font-italic">${parts[0]}</span>
      ${parts[1]}
      <span class="font-weight-bold">${parts[2]}</span>
      ${parts[3]}
    `;
    },
    canDoRatio(): string {
      const dialog = this.dialog as DialogWithRelations | null;
      if (!dialog) {
        return "0/0";
      }
      return getCanDoScoreRatio(dialog) || "0/0";
    },
    sortedReviewCards() {
      const dialog = this.dialog as DialogWithRelations | null;
      const cards = dialog?.review?.reviewCards;
      if (!cards) return [];
      return [...cards].sort((a, b) => a.id.localeCompare(b.id));
    },
    reviewPracticeRatio(): string {
      const dialog = this.dialog as DialogWithRelations | null;
      if (!dialog) {
        return "";
      }
      return getReviewPracticeCount(dialog) || "";
    },
    sortedCanDos(): CanDoListItem[] {
      const dialog = this.dialog as DialogWithRelations | null;
      if (
        !dialog?.canDo ||
        !dialog.canDo.success ||
        !dialog.canDo.result?.canDos
      ) {
        return [];
      }
      return [...dialog.canDo.result.canDos]
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
        .map((item) => {
          const achievement = toAchievement(item);
          const { icon, color, labelKey } = STATUS_STYLES[achievement];
          return {
            canDo: item.canDo,
            achievement,
            icon,
            color,
            achievementLabel: this.$t(labelKey) as string,
          };
        });
    },
  },
  methods: {
    cefrStyleClass,

    getCefrLabel(categoryData: CategoryWithDescription): string {
      return categoryData.cefrLevel || categoryData.label;
    },
    /**
     * Updates the table headers for the CEFR Table.
     */
    updateHeaders() {
      this.cefrTableHeaders = [
        this.$t("conversationAnalysis.cefrTableHeaders.overall") as string,
        this.$t("conversationAnalysis.cefrTableHeaders.range") as string,
        this.$t("conversationAnalysis.cefrTableHeaders.accuracy") as string,
        this.$t("conversationAnalysis.cefrTableHeaders.phonology") as string,
        this.$t("conversationAnalysis.cefrTableHeaders.fluency") as string,
        this.$t("conversationAnalysis.cefrTableHeaders.coherence") as string,
        this.$t("conversationAnalysis.cefrTableHeaders.interaction") as string,
      ];
      this.transcriptTableHeaders = [
        this.$t("conversationAnalysis.transcriptTableHeaders.topic") as string,
        this.$t("conversationAnalysis.transcriptTableHeaders.turnId") as string,
        this.$t(
          "conversationAnalysis.transcriptTableHeaders.startTime",
        ) as string,
        this.$t(
          "conversationAnalysis.transcriptTableHeaders.endTime",
        ) as string,
        this.$t(
          "conversationAnalysis.transcriptTableHeaders.utteranceText",
        ) as string,
      ];
    },
    /**
     * Populates the CEFR (Common European Framework of Reference) table with scores.
     */
    populateCefrTable(scores: AssessmentData) {
      this.cefrTableBody = [
        {
          label: this.getCefrLabel(scores.cefrResults.overall),
          value: this.roundToTwoDecimals(scores.cefrResults.overall.score),
        },
        {
          label: this.getCefrLabel(scores.cefrResults.range),
          value: this.roundToTwoDecimals(scores.cefrResults.range.score),
        },
        {
          label: this.getCefrLabel(scores.cefrResults.accuracy),
          value: this.roundToTwoDecimals(scores.cefrResults.accuracy.score),
        },
        {
          label: this.getCefrLabel(scores.cefrResults.phonology),
          value: this.roundToTwoDecimals(scores.cefrResults.phonology.score),
        },
        {
          label: this.getCefrLabel(scores.cefrResults.fluency),
          value: this.roundToTwoDecimals(scores.cefrResults.fluency.score),
        },
        {
          label: this.getCefrLabel(scores.cefrResults.coherence),
          value: this.roundToTwoDecimals(scores.cefrResults.coherence.score),
        },
        {
          label:
            scores.cefrResults.interaction.score === -1
              ? (this.$t("conversationAnalysis.unrated") as string)
              : this.getCefrLabel(scores.cefrResults.interaction),
          value:
            scores.cefrResults.interaction.score !== -1
              ? this.roundToTwoDecimals(scores.cefrResults.interaction.score)
              : -1,
        },
      ];
    },
    async fetchTranscript() {
      try {
        const transcriptData = await dialogService.getTranscriptById(
          this.dialogId,
        );
        if (!transcriptData || !transcriptData.transcript) {
          console.error("No transcript data available");
          this.transcriptTableBody = [];
          return;
        }
        const rows = transcriptData.transcript.trim().split("\n").slice(1);

        const topics = new Map<string, number>();
        let currentTopicIndex = 0;

        this.transcriptTableBody = rows.map((row: string, index: number) => {
          const [startTime, endTime, topic, speaker, text] = row.split("\t");
          const icon = speaker === "system" ? "mdi-robot" : "mdi-account";
          let isFirstInTopic = false;
          let rowSpan = 1;

          if (!topics.has(topic)) {
            currentTopicIndex++;
            topics.set(topic, currentTopicIndex);
            isFirstInTopic = true;

            rowSpan = rows.filter((r: string) =>
              r.includes(`\t${topic}\t`),
            ).length;
          }

          return {
            topic: `Topic-${topics.get(topic)?.toString().padStart(2, "0")}`,
            turnId: (index + 1).toString().padStart(3, "0"),
            startTime,
            endTime,
            icon,
            text,
            isFirstInTopic,
            rowSpan,
          };
        });
      } catch (error) {
        console.error("Error fetching transcript:", error);
        this.transcriptTableBody = [];
      }
    },
    roundToTwoDecimals,
    async getAudioTranscript() {
      try {
        const response = (await dialogService.getDialogAudioById(
          this.dialogId,
        )) as AudioFileResponse;

        const binaryData = response.audioData;
        const base64Data = binaryData.split(",")[1] || binaryData;

        const byteCharacters = window.atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);

        const blob = new Blob([byteArray]);
        this.audio = URL.createObjectURL(blob);
      } catch (error) {
        this.isAudioPlayerShown = false;
        console.error("Error decoding base64 audio:", error);
      }
    },
  },
});
</script>
